"use client";

import { useState, useEffect, useRef } from "react";
import {
  Paperclip,
  Sparkles,
  ArrowRight,
  Github,
  Figma,
  Send,
  Mic,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { Section } from "./core/section/Section";
import { InteractiveButton } from "./core/button/InteractiveButton";
import { CTAButton } from "./ui/CTAButton";
import { INITIAL_CREDIT } from "@/config/stripe";
import useEditorStore from "@/store/editorStore";
import { SUGGESTIONS } from "@/constants/prompts/suggestions";

const websitePrompts = [
  "Build a portfolio website for a photographer",
  "Design a landing page for a mobile app",
  "Create a restaurant menu website with HTML/CSS",
  "Launch a product site with pricing and testimonials",
  "Develop a blog using Tailwind and Markdown",
  "Create a marketing site with SEO-optimized HTML",
  "Build a static business site with contact form",
];
interface AIPromptBox {
  isAuthed: boolean;
}
export function AIPromptBox({ isAuthed }: AIPromptBox) {
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(80);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const router = useRouter();
  const { setIsDemo } = useEditorStore();

  useEffect(() => {
    if (isUserTyping) return;

    const fullPrompt = websitePrompts[currentIndex];

    if (!isDeleting && currentPrompt.length < fullPrompt.length) {
      setTypingSpeed(80);
      const timeout = setTimeout(() => {
        setCurrentPrompt(fullPrompt.substring(0, currentPrompt.length + 1));
      }, typingSpeed);
      return () => clearTimeout(timeout);
    }

    if (!isDeleting && currentPrompt === fullPrompt) {
      const timeout = setTimeout(() => setIsDeleting(true), 2000);
      return () => clearTimeout(timeout);
    }

    if (isDeleting && currentPrompt.length > 0) {
      setTypingSpeed(40);
      const timeout = setTimeout(() => {
        setCurrentPrompt(fullPrompt.substring(0, currentPrompt.length - 1));
      }, typingSpeed);
      return () => clearTimeout(timeout);
    }

    if (isDeleting && currentPrompt.length === 0) {
      setIsDeleting(false);
      setCurrentIndex((prev) => (prev + 1) % websitePrompts.length);
    }
  }, [currentPrompt, isDeleting, currentIndex, typingSpeed, isUserTyping]);

  const handleSubmit = () => {
    if (currentPrompt.trim()) {
      localStorage.setItem("raffaello-ai-prompt-box", currentPrompt.trim());
      console.log(`------currentPrompt: ${currentPrompt}`);
      setIsDemo(true);
    }

    if (isAuthed) {
      router.push("/dashboard");
    } else {
      router.push("/editor/demo");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Section className="bg-grid">
      <motion.div
        className="w-full max-w-4xl mx-auto px-6 flex flex-col items-center py-32"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <motion.h1
          className="text-4xl md:text-6xl font-extrabold text-center mb-4 bg-gradient-to-r from-blue-500 via-purple-100 to-purple-500 bg-clip-text text-transparent text-gradient text-gradient"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {/* The first Code, No-Code, and Low-Code <br />
          AI Web Builder */}
          {/* Build Websites Your Way in minutes */}
          From Idea to Website in Minutes
        </motion.h1>

        <p className="text-lg text-muted-foreground text-center mb-8 max-w-2xl">
          {/* Build your next website visually, by voice, or in code. Connect your
          repo or start from templates. Own every line. */}
          Create Websites by talking to raffaello, with drag and drop or with standard code,
          raffaello helps you build faster, with complete creative and technical freedom.
        </p>

        <div className="w-full relative mb-10">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/20 to-accent/20 rounded-lg blur-sm" />
          <motion.div
            className="relative w-full border border-border/40 rounded-xl bg-card/80 backdrop-blur-sm overflow-hidden shadow-lg"
            whileHover={{ scale: 1.01 }}
          >
            <div className="flex items-start p-4">
              <textarea
                ref={inputRef}
                value={currentPrompt}
                onFocus={() => setIsUserTyping(true)}
                onChange={(e) => setCurrentPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                // className="w-full min-h-[120px] bg-transparent resize-none focus:outline-none text-foreground placeholder:text-muted-foreground pr-12 text-lg leading-relaxed"
                className="w-full min-h-[120px] bg-transparent resize-none focus:outline-none text-foreground placeholder:text-muted-foreground pr-12"
                placeholder="Describe what you want to build..."
              />
              <div className="absolute bottom-3 right-3 flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  aria-label="Voice input"
                  isStatic={true}
                >
                  <Mic className="h-4 w-4 text-muted-foreground" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  aria-label="Generate with AI"
                  isStatic={true}
                >
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={handleSubmit}
                  aria-label="Send"
                  isStatic={true}
                >
                  <Send className="h-5 w-5 text-primary" />
                </Button>
              </div>

              <div className="ml-1 text-primary font-mono text-2xl animate-pulse">
                |
              </div>
            </div>
          </motion.div>
        </div>

        {/* <InteractiveButton
          onClick={handleCTA}
          size="lg"
          className="mb-8 px-8 py-6 text-lg font-semibold rounded-full bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-lg hover:scale-105 transition-all"
        >
          
          <ArrowRight className="ml-2 h-5 w-5" />
        </InteractiveButton> */}
        <div className="text-center justify-center items-center flex mb-10">
          {isAuthed ? (
            <CTAButton
              text="Go to your projects"
              // href="/dashboard"
              onClickAction={handleSubmit}
              className="text-xl font-bold p-4"
              tooltip="View, Edit your projects"
            ></CTAButton>
          ) : (
            <CTAButton
              text={`Start for free with ${INITIAL_CREDIT} credits`}
              // href="/signin"
              onClickAction={handleSubmit}
              className="text-xl font-bold p-4"
              tooltip="Create a new project"
            ></CTAButton>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-2">
          {SUGGESTIONS.map((suggestion) => (
            <Button
              key={suggestion}
              variant="outline"
              className="rounded-full text-sm hover:bg-primary/10"
              isStatic={true}
              onClick={() => {
                setIsUserTyping(true);
                setCurrentPrompt(`Generate a ${suggestion}`);
              }}
            >
              {suggestion}
            </Button>
          ))}
        </div>
      </motion.div>
    </Section>
  );
}
